import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { noOp } from '@neslotech/utils';

import { useAppDispatch, useAppSelector } from '../hooks';

import { ClientActions } from '../actions/client/client.actions';
import { MandateActions } from '../actions/mandate/mandate.actions';

import type { Client } from '../interfaces/client/client.interface';
import type { Mandate } from '../interfaces/client/mandate.interface';
import type { User } from '../interfaces/user/user.interface';

import Topbar from './components/Topbar';
import Icon from './components/Icon';
import FilterChipStrip from './components/FilterChipStrip';
import SegmentedControl from './components/SegmentedControl';
import ClientCard, { ClientTier } from './components/ClientCard';

import CreateClientModal from './CreateClientModal';
import { initialsFromName, deriveTier, yearOf } from './lib/mandate-helpers';
import { useTableSort, SortableTh } from './lib/use-table-sort';
import { exportCsv, type CsvColumn } from './lib/csv-export';

// =============================================================================
//  Clients list — v3 redesign
//
//  Available on the back-end:    id, name, sector, geographic_focus,
//                                personnel (User[]), category, created_at
//  Stubbed deterministically:    tier (Strategic / Growth / Emerging),
//                                relationship — not shown in pilot
//  Derived:                      mandates count (client-side from
//                                already-loaded global mandates store),
//                                since (year of created_at),
//                                lead (first personnel.full_name)
// =============================================================================

const VIEW_OPTIONS = [
  { value: 'cards' as const, icon: 'cards' as const, ariaLabel: 'Cards' },
  { value: 'rows' as const, icon: 'rows' as const, ariaLabel: 'Rows' }
];

type TierKey = 'All' | ClientTier;

const TIER_FILTERS: ReadonlyArray<{ value: TierKey; label: string }> = [
  { value: 'All', label: 'All' },
  { value: 'Strategic', label: 'Strategic' },
  { value: 'Growth', label: 'Growth' },
  { value: 'Emerging', label: 'Emerging' }
];

const leadName = (personnel: Client['personnel']): string | undefined => {
  if (!personnel || typeof personnel === 'string' || personnel.length === 0) return undefined;
  const first = personnel[0] as User;
  // Prefer full_name when present; fall back to first/last composite.
  const fn = (first as User & { full_name?: string }).full_name;
  if (fn) return fn;
  if (first.first_name || first.last_name)
    return `${first.first_name ?? ''} ${first.last_name ?? ''}`.trim();
  return undefined;
};

/** Pure (non-hook) derivation of per-client display values. */
const deriveClientView = (client: Client, mandates: Mandate[]) => {
  const tier = deriveTier(client);
  const mandatesCount = mandates.filter((m) => String(m.client_id) === String(client.id)).length;
  const since = yearOf(client.created_at);
  const lead = leadName(client.personnel);
  return { tier, mandatesCount, since, lead };
};

const ClientsListV3 = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const clients: Client[] = useAppSelector(({ client_store }) => client_store.clients) || [];
  const mandates: Mandate[] = useAppSelector(({ mandate_store }) => mandate_store.mandates) || [];

  const [view, setView] = useState<'cards' | 'rows'>('cards');
  const [tierFilter, setTierFilter] = useState<TierKey>('All');
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  // Track whether the initial /clients load has completed.  The endpoint
  // serialises 900+ rows with relationships and can take ~10–15s on a cold
  // run; without this flag the page renders "No clients match these filters"
  // for the full duration of the request, which reads as a broken page.
  // If the store is already populated from a prior visit we skip the
  // skeleton entirely.
  const [loaded, setLoaded] = useState(clients.length > 0);

  useEffect(() => {
    dispatch(ClientActions.loadClients(() => setLoaded(true)));
    dispatch(MandateActions.loadMandates(noOp));
  }, [dispatch]);

  // Pre-compute the per-client view to avoid recomputing on every filter/sort.
  const enriched = useMemo(
    () =>
      clients.map((c) => ({
        client: c,
        view: deriveClientView(c, mandates)
      })),
    [clients, mandates]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return enriched.filter(({ client, view: v }) => {
      if (tierFilter !== 'All' && v.tier !== tierFilter) return false;
      if (q) {
        const hay =
          `${client.name ?? ''} ${client.sector ?? ''} ${client.geographic_focus ?? ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [enriched, tierFilter, search]);

  const SORT_ACCESSORS = useMemo(
    () => ({
      client: (r: any) => r.client?.name,
      sector: (r: any) => r.client?.sector,
      region: (r: any) => r.client?.geographic_focus || r.client?.location,
      tier: (r: any) => r.view?.tier,
      mandates: (r: any) => r.view?.mandatesCount ?? 0,
      since: (r: any) => r.view?.since,
      lead: (r: any) => r.view?.lead
    }),
    []
  );
  const { sorted, sort, toggle } = useTableSort(filtered, SORT_ACCESSORS);

  const strategicCount = useMemo(
    () => enriched.filter((e) => e.view.tier === 'Strategic').length,
    [enriched]
  );

  // Client-side CSV.  No /clients.csv endpoint exists on the backend (the
  // server-side export is per-client and lives at /clients/:id/export.csv);
  // we generate a CSV from the in-memory list here.  When a bulk endpoint
  // is added, swap to a download saga and delete this column definition.
  const handleExport = () => {
    const columns: ReadonlyArray<CsvColumn<(typeof enriched)[0]>> = [
      { header: 'Name', accessor: (e) => e.client.name },
      { header: 'Tier', accessor: (e) => e.view.tier ?? '' },
      { header: 'Category', accessor: (e) => e.client.category },
      { header: 'Sector', accessor: (e) => e.client.sector },
      { header: 'Geographic focus', accessor: (e) => e.client.geographic_focus },
      { header: 'Location', accessor: (e) => e.client.location },
      { header: 'Mandates (active)', accessor: (e) => e.view.mandatesCount },
      { header: 'Lead', accessor: (e) => e.view.lead },
      { header: 'Since', accessor: (e) => e.view.since },
      { header: 'Client type', accessor: (e) => e.client.client_type as string | undefined }
    ];
    // Respect the current filters so users can export "Strategic clients"
    // by clicking the chip first and then Export.
    exportCsv(filtered, columns, 'Clients');
  };

  return (
    <>
      <Topbar
        crumbs={['Deal Flow', 'Clients']}
        tools={
          <button
            className="btn ghost"
            type="button"
            onClick={handleExport}
            disabled={!loaded || clients.length === 0}
            title={
              filtered.length === clients.length
                ? `Download CSV of all ${clients.length} clients`
                : `Download CSV of ${filtered.length} filtered clients`
            }
          >
            <Icon name="download" size={14} />
            Export
          </button>
        }
      />
      <div className="page">
        <div className="page-header">
          <div>
            <div className="eyebrow">Relationships</div>
            <h1 className="page-title">Clients</h1>
            <div className="page-sub">
              {!loaded && clients.length === 0
                ? 'Loading…'
                : filtered.length === clients.length
                  ? `${clients.length} clients · ${strategicCount} strategic`
                  : `${filtered.length} of ${clients.length} clients · ${strategicCount} strategic`}
            </div>
          </div>
          <div className="page-tools">
            <label className="search">
              <Icon name="search" size={14} />
              <input
                type="text"
                placeholder="Search clients…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </label>
            <SegmentedControl<'cards' | 'rows'>
              options={VIEW_OPTIONS}
              value={view}
              onChange={setView}
            />
            <button className="btn primary" type="button" onClick={() => setCreating(true)}>
              <Icon name="plus" size={14} />
              New client
            </button>
          </div>
        </div>

        <FilterChipStrip<TierKey>
          options={TIER_FILTERS}
          value={tierFilter}
          onChange={setTierFilter}
        />

        {!loaded && clients.length === 0 ? (
          <div className="card" style={{ padding: 48, textAlign: 'center' }}>
            <span className="muted">Loading clients…</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="card" style={{ padding: 48, textAlign: 'center' }}>
            <span className="muted">
              {clients.length === 0 ? 'No clients yet.' : 'No clients match these filters.'}
            </span>
          </div>
        ) : view === 'cards' ? (
          <div className="grid cards-3">
            {filtered.map(({ client, view: v }) => (
              <ClientCard
                key={client.id}
                name={client.name || 'Unnamed client'}
                sector={client.sector}
                region={client.geographic_focus || client.location}
                tier={v.tier}
                mandates={v.mandatesCount}
                since={v.since}
                lead={v.lead}
                onClick={() => navigate(`/clients/${client.id}`)}
              />
            ))}
          </div>
        ) : (
          <div className="card flush">
            <table className="tbl">
              <thead>
                <tr>
                  <SortableTh label="Client" sortKey="client" sort={sort} onToggle={toggle} />
                  <SortableTh label="Sector" sortKey="sector" sort={sort} onToggle={toggle} />
                  <SortableTh label="Region" sortKey="region" sort={sort} onToggle={toggle} />
                  <SortableTh label="Tier" sortKey="tier" sort={sort} onToggle={toggle} />
                  <SortableTh
                    label="Mandates"
                    sortKey="mandates"
                    sort={sort}
                    onToggle={toggle}
                    className="right"
                  />
                  <SortableTh label="Since" sortKey="since" sort={sort} onToggle={toggle} />
                  <SortableTh label="Lead partner" sortKey="lead" sort={sort} onToggle={toggle} />
                </tr>
              </thead>
              <tbody>
                {sorted.map(({ client, view: v }) => (
                  <tr
                    key={client.id}
                    onClick={() => navigate(`/clients/${client.id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td className="strong">
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span
                          style={{
                            width: 30,
                            height: 30,
                            borderRadius: 4,
                            background: 'var(--ink)',
                            color: 'var(--gold-soft)',
                            display: 'inline-grid',
                            placeItems: 'center',
                            fontFamily: 'var(--font-display)',
                            fontSize: 11,
                            fontWeight: 600,
                            letterSpacing: 0.3
                          }}
                        >
                          {initialsFromName(client.name)}
                        </span>
                        {client.name}
                      </div>
                    </td>
                    <td>{client.sector || '—'}</td>
                    <td>{client.geographic_focus || client.location || '—'}</td>
                    <td>
                      {v.tier ? (
                        <span
                          className={`chip ${v.tier === 'Strategic' ? 'gold' : v.tier === 'Growth' ? 'info' : 'muted'}`}
                        >
                          {v.tier}
                        </span>
                      ) : (
                        <span className="muted">Not set</span>
                      )}
                    </td>
                    <td className="num right">{v.mandatesCount}</td>
                    <td className="mono">{v.since}</td>
                    <td>{v.lead || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {creating && <CreateClientModal onClose={() => setCreating(false)} />}
    </>
  );
};

export default ClientsListV3;
